// frontend/src/pages/asset-admin/AssetAdminChecklists.js
/**
 * Asset Admin - Checklists Management Page
 * 
 * Manage onboarding and offboarding asset checklists.
 * Theme: Red (Admin)
 */

import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ClipboardCheck, UserPlus, UserMinus, Calendar, 
  CheckCircle, Clock, AlertCircle, ChevronRight
} from 'lucide-react';
import { useTabPersistence } from '../../hooks/usePersistence';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import { usePendingChecklists } from '../../hooks/useAssetInventoryData';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { FilterButton } from '../../components/UI/FilterButton';

// ============================================
// CONSTANTS
// ============================================
const VALID_TABS = ['all', 'pending', 'in_progress', 'completed'];

// ============================================
// CHECKLIST TYPE BADGE
// ============================================
const ChecklistTypeBadge = ({ type }) => {
  const isOnboarding = type === 'onboarding';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
      isOnboarding 
        ? 'bg-green-100 text-green-700' 
        : 'bg-red-100 text-red-700'
    }`}>
      {isOnboarding ? <UserPlus className="w-3 h-3" /> : <UserMinus className="w-3 h-3" />}
      {isOnboarding ? 'Onboarding' : 'Offboarding'}
    </span>
  );
};

// ============================================
// PROGRESS BAR
// ============================================
const ProgressBar = ({ percentage }) => {
  return (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div 
        className={`h-2 rounded-full transition-all ${
          percentage >= 100 ? 'bg-green-500' : 
          percentage >= 50 ? 'bg-blue-500' : 
          'bg-yellow-500'
        }`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
  );
};

// ============================================
// CHECKLIST CARD
// ============================================
const ChecklistCard = ({ checklist }) => {
  const completedItems = checklist.total_items - checklist.pending_items;
  const percentage = checklist.total_items > 0 
    ? Math.round((completedItems / checklist.total_items) * 100) 
    : 0;
  
  return (
    <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            checklist.checklist_type === 'onboarding' 
              ? 'bg-green-100' 
              : 'bg-red-100'
          }`}>
            {checklist.checklist_type === 'onboarding' 
              ? <UserPlus className="w-5 h-5 text-green-600" />
              : <UserMinus className="w-5 h-5 text-red-600" />
            }
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900">{checklist.employee_name}</p>
            <p className="text-xs text-gray-500">{checklist.employee_staff_id}</p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={checklist.status} />
        </div>
      </div>

      <div className="space-y-3 flex-1">
        <div className="flex items-center justify-between text-sm">
          <ChecklistTypeBadge type={checklist.checklist_type} />
          <div className="flex items-center gap-1 text-gray-500">
            <Calendar className="w-3 h-3" />
            {new Date(checklist.effective_date).toLocaleDateString()}
          </div>
        </div>

        {/* Progress */}
        <div>
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{completedItems}/{checklist.total_items} items ({percentage}%)</span>
          </div>
          <ProgressBar percentage={percentage} />
        </div>
      </div>
      
      {/* Action */}
      <div className="card-action-footer">
        <button className="w-full btn btn-admin-outline btn-sm flex items-center justify-center gap-2">
          <ClipboardCheck className="w-4 h-4" />
          View Details
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const AssetAdminChecklists = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useTabPersistence('asset-admin-checklists', 'pending', VALID_TABS);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('staff_id') || '');
  const [typeFilter, setTypeFilter] = useState(
    ['onboarding', 'offboarding'].includes(searchParams.get('type'))
      ? searchParams.get('type')
      : '',
  );
  const [showFilters, setShowFilters] = useState(Boolean(searchParams.get('type')));
  
  // Fetch checklists
  const { data: checklistsData, isLoading, error } = usePendingChecklists({ status: activeTab === 'all' ? undefined : activeTab });
  
  const checklists = useMemo(
    () => checklistsData?.results || checklistsData || [],
    [checklistsData],
  );
  
  // Filter checklists
  const filteredChecklists = useMemo(() => {
    let filtered = checklists;
    
    // Filter by type
    if (typeFilter) {
      filtered = filtered.filter(c => c.checklist_type === typeFilter);
    }
    
    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.employee_name?.toLowerCase().includes(query) ||
        c.employee_staff_id?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [checklists, typeFilter, searchQuery]);
  
  // Tab definitions
  const tabs = [
    { id: 'all', name: 'All Checklists', icon: ClipboardCheck },
    { id: 'pending', name: 'Pending', icon: Clock },
    { id: 'in_progress', name: 'In Progress', icon: AlertCircle },
    { id: 'completed', name: 'Completed', icon: CheckCircle },
  ];

  // Stats
  const onboardingCount = filteredChecklists.filter(c => c.checklist_type === 'onboarding').length;
  const offboardingCount = filteredChecklists.filter(c => c.checklist_type === 'offboarding').length;

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <ClipboardCheck className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Asset Checklists</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">Manage onboarding and offboarding checklists</p>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="flex gap-3">
            <div className="px-4 py-2 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">{onboardingCount} Onboarding</span>
              </div>
            </div>
            <div className="px-4 py-2 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2">
                <UserMinus className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-700">{offboardingCount} Offboarding</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
            </button>
          );
        })}
      </div>

{/* Filters */}
<div className="bg-white rounded-2xl shadow-sm border p-4">
  <div className="flex items-center justify-between gap-3">
    {/* Left: Search */}
    <DebouncedSearchInput
      value={searchQuery}
      onSearch={setSearchQuery}
      placeholder="Search by employee name or ID..."
      className="max-w-xl"
    />

    {/* Right: Filter */}
    <div className="flex items-center gap-2">
      <FilterButton
        theme="admin"
        isOpen={showFilters}
        activeCount={typeFilter ? 1 : 0}
        onClick={() => setShowFilters(v => !v)}
      />
    </div>
  </div>

  {/* Collapsible filter panel */}
  {showFilters && (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="form-label-admin">Type</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="select-admin w-full"
          >
            <option value="">All Types</option>
            <option value="onboarding">Onboarding</option>
            <option value="offboarding">Offboarding</option>
          </select>
        </div>
      </div>
    </div>
  )}
</div>

      {/* Checklists Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="bg-white rounded-xl border p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
                <div className="h-6 bg-gray-200 rounded-full w-20"></div>
              </div>
              <div className="space-y-2 mb-3">
                <div className="h-2 bg-gray-200 rounded-full"></div>
                <div className="h-3 bg-gray-200 rounded w-16"></div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="h-4 bg-gray-200 rounded w-20"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-gray-500">Failed to load checklists</p>
        </div>
      ) : filteredChecklists.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No checklists found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredChecklists.map(checklist => (
            <ChecklistCard key={checklist.id} checklist={checklist} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AssetAdminChecklists;
